import { Box } from '@mui/material';
import SavePaymentInstrumentCheckbox from 'components/payment/spreedly/spreedly-card/forms/fields/save-payment-instrument-checkbox.component';
import SpreedlyGlobalErrors from 'components/payment/spreedly/spreedly-card/forms/spreedly-global-errors/spreedly-global-errors.component';
import SpreedlyContext from 'components/payment/spreedly/spreedly.context';
import { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { SpreedlyCheckoutSdkDefault } from './spreedly-checkout-sdk-default.component';
import { Provider as SpreedlyCheckoutSdkFormProvider } from './spreedly-checkout-sdk-form.context';
import { useSpreedlyCheckoutSdkForm } from './spreedly-checkout-sdk-form.hooks';
import { SpreedlyCheckoutSdkToast } from './spreedly-checkout-sdk-toast.component';
import Form from 'components/form/form.component';
import IncButton from 'components/inc-button/inc-button.component';
import { useTranslation } from 'react-i18next';

export const spreedlyCheckoutSdkNumberName =
  'spreedly-checkout-sdk-card-number';
export const spreedlyCheckoutSdkCvvName = 'spreedly-checkout-sdk-cvv';
export const spreedlyCheckoutSdkIframeFields = {
  [spreedlyCheckoutSdkNumberName]: 'number',
  [spreedlyCheckoutSdkCvvName]: 'cvv',
};

const SpreedlyCheckoutSdkForm = ({
  formName,
  initOrderAttributes,
  onSuccess,
  onError,
  onSubmitStart,
  showSaveCardCheckbox,
  button,
  disabled,
}) => {
  const { t } = useTranslation();
  const { handleSubmit } = useFormContext();
  // Optional: only the full web checkout tree provides SpreedlyContext, so an
  // external "Place Order" button elsewhere can trigger this form's submit.
  // Standalone consumers (e.g. a hosted page rendering its own submit button
  // via `button` below) have no such external trigger and no provider to
  // match — registering is simply skipped for them.
  const { setSubmitSpreedlyForm } = useContext(SpreedlyContext) ?? {};
  const value = useSpreedlyCheckoutSdkForm(
    initOrderAttributes,
    onSuccess,
    onError,
  );
  const { submit, validate } = value;

  const handleFormSubmit = handleSubmit(
    data => {
      onSubmitStart?.();
      submit(data);
    },
    // RHF's own validation failed (e.g. a required field left blank), so
    // submit() above never runs — explicitly validate the card/CVV iframe
    // fields too so a number/CVV the user never touched still reports an
    // error instead of just silently never being checked.
    () => {
      validate();
    },
  );

  useEffect(() => {
    setSubmitSpreedlyForm?.(_state => handleFormSubmit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box data-testid="SpreedlyCard.Container">
      <Form
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
          handleFormSubmit();
        }}
        noValidate
        id="payment-form"
      >
        <SpreedlyCheckoutSdkFormProvider value={value}>
          <Box
            display="grid"
            rowGap={theme => theme.spacing(2)}
            sx={{
              '& .MuiInputLabel-root': {
                whiteSpace: 'nowrap',
              },
            }}
          >
            <SpreedlyCheckoutSdkFormSwitch formName={formName} />
            {showSaveCardCheckbox && <SavePaymentInstrumentCheckbox />}
          </Box>
        </SpreedlyCheckoutSdkFormProvider>
        {button && (
          <IncButton
            type="submit"
            data-testid="SpreedlyCard.CustomButton"
            fullWidth
            disabled={disabled}
          >
            {t('checkout.payment.spreedly.submit')}
          </IncButton>
        )}
        <SpreedlyGlobalErrors />
      </Form>
    </Box>
  );
};

export const SpreedlyCheckoutSdkFormSwitch = ({ formName }) => {
  switch (formName) {
    case 'spreedly':
    case 'spreedly_condenced_enhanced':
      return <SpreedlyCheckoutSdkDefault />;
    case 'toast_spreedly':
    case 'spreedly_enhanced':
    case 'toast_spreedly_canada':
    case 'spreedly_canada_enhanced':
      return <SpreedlyCheckoutSdkToast />;
    default:
      throw new Error('Spreedly Configuration Error - Unknown Form Name');
  }
};

export default SpreedlyCheckoutSdkForm;
